// MotoDoctorBlogs.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import "../scss/blog.css";

const blogs = [
  {
    title: "Top 5 Reasons Chennai Riders Love Doorstep Bike Service",
    intro:
      "Long queues at workshops, waiting under the sun, and losing half your day? That’s why thousands of riders in Chennai are switching to MotoDoctor's doorstep bike service. Here’s why it’s a game-changer.",
    keywords: ["doorstep bike service Chennai", "on-demand bike repair", "motorcycle service at home"],
    sections: [
      {
        heading: "1. Ultimate Convenience",
        content: "MotoDoctor brings trained mechanics to your home, office, or anywhere you are.",
      },
      {
        heading: "2. Saves Time & Effort",
        content: "Avoid traffic, waiting, and workshop delays. Book in minutes through the app.",
      },
      {
        heading: "3. Transparent Pricing",
        content: "Know the cost upfront, pay online or cash — no hidden charges.",
      },
      {
        heading: "4. Verified Mechanics",
        content: "All mechanics are background-checked & experienced in multiple bike brands.",
      },
      {
        heading: "5. Genuine Spare Parts & Warranty",
        content: "MotoDoctor uses genuine parts, and your service comes with support if there’s an issue.",
      },
    ],
    conclusion:
      "In a city like Chennai, where time and safety matter, doorstep bike service isn’t a luxury — it’s the smarter way to keep your ride road-ready.",
  },
  {
    title: "What to Do if Your Bike Breaks Down at Night in Chennai",
    intro:
      "It’s late, roads are empty, and your bike suddenly stops. Don’t panic — follow these steps to stay safe and get help quickly.",
    keywords: ["bike roadside assistance Chennai", "motorcycle breakdown help", "emergency towing Chennai"],
    sections: [
      {
        heading: "1. Move to a Safe Spot",
        content: "If possible, push your bike to the roadside or under a streetlight.",
      },
      {
        heading: "2. Turn on Hazard Lights / Reflectors",
        content: "Make yourself visible to other drivers.",
      },
      {
        heading: "3. Check Simple Fixes",
        content: "Loose battery terminal, empty fuel, or kill switch turned off? Sometimes it’s an easy fix.",
      },
      {
        heading: "4. Call for 24/7 Help",
        content:
          "Open the MotoDoctor app and request roadside assistance. We’ll send a mechanic or towing in 30–45 minutes anywhere in Chennai.",
      },
      {
        heading: "5. Share Location with Family / Friend",
        content: "Always let someone know where you are.",
      },
    ],
    conclusion:
      "Night breakdowns can feel scary, but with MotoDoctor’s 24/7 service, help is always a tap away. Stay calm, stay safe, and let us bring you back on the road.",
  },
  {
    title: "Complete Guide: MotoDoctor Services Every Chennai Rider Should Know",
    intro:
      "MotoDoctor isn’t just for emergencies. Here’s a full list of services you can use — so your bike stays in top shape year-round.",
    keywords: ["bike repair Chennai", "motorcycle towing", "roadside help Chennai", "on-demand mechanic"],
    sections: [
      {
        heading: "Emergency Roadside Assistance",
        content: "Flat tires, battery jumpstart, fuel delivery, or sudden breakdowns — we’ve got you covered 24/7.",
      },
      {
        heading: "Doorstep Bike Service & Repairs",
        content: "General service, oil change, brake or clutch issues — fixed at your location with real-time updates.",
      },
      {
        heading: "Pickup & Drop",
        content:
          "Don’t have time? We’ll collect your bike, service it, and return it back to you in perfect condition.",
      },
      {
        heading: "Towing & Transport",
        content: "Accident? Engine issue? We safely tow your bike to a workshop or your preferred location.",
      },
      {
        heading: "Spare Parts Support",
        content: "Choose from original parts or cost-effective third-party spares.",
      },
      {
        heading: "Real-time Tracking",
        content: "See where your mechanic is and chat inside the app.",
      },
    ],
    conclusion: "Whether it’s an urgent breakdown or routine service, MotoDoctor keeps Chennai riders moving.",
  },
  {
    title: "Top 7 Bike Problems Faced by Chennai Riders — and How to Fix Them",
    intro:
      "Chennai’s traffic and climate can put extra stress on your two-wheeler. Here are the 7 most common problems Chennai riders face — and how MotoDoctor helps fix them fast.",
    keywords: ["common bike problems", "bike repair Chennai", "motorcycle issues"],
    sections: [
      {
        heading: "1. Flat or punctured tire",
        content: "Caused by bad roads & debris; solved with our mobile puncture repair & replacement.",
      },
      {
        heading: "2. Battery trouble",
        content:
          "Extreme heat & frequent short rides weaken batteries. MotoDoctor offers on-site jumpstart & replacement.",
      },
      {
        heading: "3. Starting trouble",
        content: "From spark plugs to wiring; our mechanics diagnose & repair at your doorstep.",
      },
      {
        heading: "4. Chain issues",
        content: "Loose, dry, or broken chain? We adjust, lubricate, or replace as needed.",
      },
      {
        heading: "5. Clutch cable issues",
        content: "We adjust or replace clutch cables on the spot.",
      },
      {
        heading: "6. Electrical faults",
        content: "Indicators, headlight, or horn problems? We fix wiring & fuse issues.",
      },
      {
        heading: "7. Low fuel emergencies",
        content: "Stuck with no petrol? MotoDoctor delivers fuel anywhere in Chennai.",
      },
    ],
    conclusion: "Don’t let small issues turn into big problems. With MotoDoctor, help is always a tap away.",
  },
  {
    title: "Doorstep vs. Workshop Bike Service in Chennai: Which is Better?",
    intro:
      "Should you ride to a workshop or let the mechanic come to you? Here’s a side-by-side comparison to help you decide.",
    keywords: ["doorstep bike service Chennai", "workshop bike repair", "home service motorcycle"],
    sections: [
      { heading: "Convenience", content: "Doorstep service wins — no waiting, no traffic." },
      { heading: "Time saved", content: "Workshop visits often mean half a day; doorstep takes 30–90 minutes." },
      { heading: "Cost", content: "Similar, but doorstep avoids transport/pickup charges." },
      { heading: "Transparency", content: "With MotoDoctor, you watch the mechanic work on your bike." },
      {
        heading: "Complex repairs",
        content: "Workshops needed for engine overhauls; 90% of service can be done at your location.",
      },
    ],
    conclusion: "For most Chennai riders, doorstep service is faster, easier, and just as reliable.",
  },
  {
    title: "How MotoDoctor Ensures Quality Bike Repairs — Even at Your Doorstep",
    intro:
      "Worried that home service might be low quality? Here’s how MotoDoctor keeps standards high, wherever you are.",
    keywords: ["quality bike service", "doorstep mechanic Chennai", "verified bike mechanic"],
    sections: [
      { heading: "1. Verified mechanics", content: "Background-checked, skilled & trained on all major brands." },
      { heading: "2. Genuine spare parts", content: "Choose original or cost-effective options." },
      { heading: "3. Digital job card", content: "See what was done; full service record in your app." },
      { heading: "4. Standardized process", content: "Every mechanic follows MotoDoctor’s checklist." },
      { heading: "5. Feedback-driven improvement", content: "Rate service & share feedback; we act on it." },
    ],
    conclusion: "MotoDoctor brings workshop-level quality to your doorstep.",
  },
  {
    title: "Need Bike Roadside Help in Chennai? Here’s How MotoDoctor Reaches You Fast",
    intro: "Breakdowns never come planned. Here’s why MotoDoctor can reach you quickly — day or night.",
    keywords: ["roadside bike assistance Chennai", "emergency towing", "fast bike help"],
    sections: [
      { heading: "Real-time mechanic tracking", content: "App locates the nearest available mechanic." },
      { heading: "Citywide network", content: "Multiple mechanics across Chennai zones." },
      { heading: "Smart dispatch", content: "Advanced algorithm matches based on your bike model & issue." },
      { heading: "Average arrival time", content: "30–45 minutes in most areas." },
      { heading: "Live updates & chat", content: "Track arrival & talk to your mechanic." },
    ],
    conclusion: "Don’t wait hours on the road. Get help fast with MotoDoctor.",
  },
  {
    title: "Your First Bike? Here’s Why You Still Need MotoDoctor in Chennai",
    intro: "Just bought your first bike? Congratulations! But even new bikes need backup — here’s why.",
    keywords: ["bike roadside help for new bikers", "motorcycle beginner tips", "Chennai bike service"],
    sections: [
      { heading: "Unexpected punctures", content: "Even brand-new tires get flats." },
      { heading: "Battery & starting trouble", content: "Factory batteries can still fail in heat." },
      { heading: "Accidents or towing", content: "A small fall can stop your ride." },
      { heading: "Scheduled service & pickup", content: "New bikes need first service — we handle pickup & drop." },
      { heading: "Peace of mind", content: "Enjoy rides, knowing help is a tap away." },
    ],
    conclusion: "New or old, every bike rider deserves quick help. Stay protected with MotoDoctor.",
  },
];

const MotoDoctorBlogs = () => {
  return (
    <main className="blog-main">
      <Helmet>
        <title>MotoDoctor Bike Blogs | Chennai Rider Help</title>
        <meta
          name="description"
          content="Explore blogs for Chennai bike riders – tips, service help, breakdown support, and more. Learn why MotoDoctor is the go-to for two-wheeler help."
        />
        <link rel="canonical" href="/blog" />
      </Helmet>
      {blogs.map((blog, index) => (
        <section key={index} className="blog-post">
          <h1>{blog.title}</h1>
          <p className="intro">{blog.intro}</p>
          {blog.sections.map((sec, i) => (
            <div key={i}>
              <h2>{sec.heading}</h2>
              <p>{sec.content}</p>
            </div>
          ))}
          <p className="conclusion">{blog.conclusion}</p>
          <div className="cta">
            ✅ Ready to ride worry-free?{" "}
            <a href="/rider" target="_blank" rel="noreferrer">
              Download MotoDoctor App
            </a>
          </div>
        </section>
      ))}
    </main>
  );
};

export default MotoDoctorBlogs;
